import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Flag } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { useLanguage, useT } from "../../lib/LanguageContext";
import usePagedList from "../../hooks/usePagedList";
import LoadFailure from "../../components/ui/LoadFailure";
import Pager from "../../components/ui/Pager";
import StarRating from "../../reviews/components/StarRating";
import { HIDE_REASON_MAX_LEN, REVIEW_PATHS } from "../../reviews/contract";
import { reviewErrorText } from "../../reviews/errorText";
import { EMPTY, fmtAvg, fmtDate } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";
import { useScreenWrites } from "../screenWrites";

// The hide reason is OPTIONAL on the wire (`HideIn.reason` defaults to "", contract §8):
// the audit row records who hid what and when regardless. The form therefore never
// blocks on it — a local minimum would forbid a value the API accepts.

/**
 * ONE practice's reviews inside the moderation console — `GET /super-admin/reviews` with
 * `tenant_id=` and the console's filters, paged on its own.
 *
 * Each row LABELS its people: the reviewer's public display name and the client's email
 * are named as such, because the practice is named once, in the section header above, and
 * an unlabelled name on a row is exactly what used to read as the reviewer when it was the
 * practice.
 *
 * HIDING RECOMPUTES IN THE SAME REQUEST (server side), so the list is re-read afterwards
 * rather than patched locally: the row's `hidden` flag is not the only thing that moved —
 * the practice's average, its histogram and, through the platform mean `C`, EVERY other
 * practice's rank did too. `onChanged` re-reads the practice headers for the same reason.
 *
 * `client_email` is shown HERE and nowhere else in the product: the admin row is the only
 * projection that carries it, and it is what lets an operator answer a "that was not me"
 * complaint. It never reaches the public row, the owner row, or any token flow.
 */
export default function PracticeReviews({ tenantId, filters, onChanged }) {
  // Hiding and unhiding a review are tenants.write; reading them is tenants.read.
  const { moderate: canModerate } = useScreenWrites("reviews");
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [hideTarget, setHideTarget] = useState(null);
  const [busyId, setBusyId] = useState("");

  const list = usePagedList(REVIEW_PATHS.adminReviews, { tenant_id: tenantId, ...filters });

  const changed = () => {
    list.reload();
    onChanged();
  };

  const unhide = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(REVIEW_PATHS.adminUnhide(row.id));
      toast.success(t("srev.unhidden"));
      changed();
    } catch (e) {
      toast.error(reviewErrorText(e, { t, tv, prefix: "srev", fallback: tv("errors.saveFailed") }));
    } finally {
      setBusyId("");
    }
  };

  if (list.loadError) {
    return (
      <div className="px-5 py-4">
        <LoadFailure
          message={reviewErrorText(list.loadError, { t, tv, prefix: "srev", fallback: tv("errors.loadFailed") })}
          onRetry={list.reload}
          retryLabel={tv("actions.retry")}
          testId={REVIEWS.saPracticeLoadError(tenantId)}
          retryTestId={`sa-reviews-practice-retry-${tenantId}`}
        />
      </div>
    );
  }
  if (!list.loaded) {
    return <p className="px-5 py-4 text-sm text-slate-400">{t("srev.loading")}</p>;
  }

  return (
    <div data-testid={REVIEWS.saPracticeReviews(tenantId)}>
      <ul className="m-0 p-0 divide-y divide-slate-100 border-t border-slate-100">
        {list.rows.map((r) => (
          <li key={r.id} className="list-none pl-12 pr-5 py-4" data-testid={REVIEWS.saRow(r.id)}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <StarRating
                value={r.rating}
                readOnly
                variant="light"
                size="sm"
                label={t("srev.row.aria", { avg: fmtAvg(r.rating) })}
              />
              <span className="text-xs text-slate-400 tabular-nums">{fmtDate(r.created_at, lang)}</span>
              {r.hidden && (
                <span
                  data-testid={REVIEWS.saHiddenPill(r.id)}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200"
                >
                  <EyeOff className="w-3 h-3" aria-hidden="true" /> {t("srev.row.hidden")}
                </span>
              )}
              {r.flag && (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
                  <Flag className="w-3 h-3" aria-hidden="true" /> {t("srev.row.flagged")}
                </span>
              )}
            </div>
            <dl className="mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5 text-xs">
              <div className="flex gap-1 min-w-0">
                <dt className="text-slate-400">{t("srev.row.reviewer")}</dt>
                <dd className="m-0 text-slate-700 break-words">{r.reviewer_display || EMPTY}</dd>
              </div>
              <div className="flex gap-1 min-w-0">
                <dt className="text-slate-400">{t("srev.row.client")}</dt>
                <dd className="m-0 text-slate-700 break-all">{r.client_email || EMPTY}</dd>
              </div>
              {r.service_name && (
                <div className="flex gap-1 min-w-0">
                  <dt className="text-slate-400">{t("srev.row.service")}</dt>
                  <dd className="m-0 text-slate-700 break-words">{r.service_name}</dd>
                </div>
              )}
            </dl>

            {r.text && (
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-line break-words">{r.text}</p>
            )}
            {r.flag?.reason && (
              <p className="text-xs text-amber-800 mt-1 break-words">
                {t("srev.row.flagReason", { reason: r.flag.reason })}
              </p>
            )}
            {r.hidden && r.hidden_reason && (
              <p className="text-xs text-slate-500 mt-1 break-words">
                {t("srev.row.hiddenReason", { reason: r.hidden_reason, by: r.hidden_by || EMPTY })}
              </p>
            )}

            {canModerate && (
            <div className="mt-3">
              {r.hidden ? (
                <button
                  type="button"
                  onClick={() => unhide(r)}
                  disabled={busyId === r.id}
                  data-testid={REVIEWS.saUnhide(r.id)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-50"
                >
                  <Eye className="w-3 h-3" aria-hidden="true" /> {t("srev.unhide")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setHideTarget(r)}
                  data-testid={REVIEWS.saHide(r.id)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700"
                >
                  <EyeOff className="w-3 h-3" aria-hidden="true" /> {t("srev.hide")}
                </button>
              )}
            </div>
            )}
          </li>
        ))}
      </ul>
      {list.total > list.pageSize && (
        <Pager
          page={list.page}
          pageSize={list.pageSize}
          total={list.total}
          shown={list.rows.length}
          onPage={list.setPage}
          infoText={(vars) => t("srev.page.info", vars)}
          prevLabel={t("srev.page.prev")}
          nextLabel={t("srev.page.next")}
          testId={`sa-reviews-practice-page-${tenantId}`}
          tone="slate"
        />
      )}

      <HideDialog
        review={hideTarget}
        t={t}
        tv={tv}
        onClose={(didHide) => {
          setHideTarget(null);
          if (didHide) changed();
        }}
      />
    </div>
  );
}

/** Reason-gated hide. One dialog per practice section; at most one is ever open. */
function HideDialog({ review, t, tv, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (review) {
      setReason("");
      setBusy(false);
    }
  }, [review]);

  const submit = async () => {
    const body = reason.trim();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(REVIEW_PATHS.adminHide(review.id), { reason: body });
      toast.success(t("srev.hidden"));
      onClose(true);
    } catch (e) {
      toast.error(reviewErrorText(e, { t, tv, prefix: "srev", fallback: tv("errors.saveFailed") }));
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!review} onOpenChange={(open) => { if (!open) onClose(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("srev.hideDialog.title")}</DialogTitle>
          <DialogDescription>{t("srev.hideDialog.desc")}</DialogDescription>
        </DialogHeader>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={HIDE_REASON_MAX_LEN}
          rows={4}
          disabled={busy}
          placeholder={t("srev.hideDialog.placeholder")}
          data-testid={REVIEWS.saHideReason}
          className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800"
        />
        <DialogFooter>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 h-9 rounded-full border border-slate-200 text-sm text-slate-700"
          >
            {tv("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={REVIEWS.saHideConfirm}
            className="px-4 h-9 rounded-full bg-rose-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? t("srev.hideDialog.submitting") : t("srev.hideDialog.confirm")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
